import React, { useEffect, useState } from 'react';
import insuranceDetailsService from '../insuranceDetails/insuranceDetailsService.js';
import PatientInfoService from '../patientInfo/patientInfoService.js';
import InsurancePatientInfoService from '../patientInfo/InsurancePatientInfoService.js';
import authorizationService from '../Authorization/authorizationService.js';
import PointsBankAPI from '../points/PointsBankAPI.js';
import DiagnosisCombinerService from '../Authorization/diagnosisCombinerService.js';
import PatientContactPermissionService from '../patientInfo/PatientContactPermissionService.js';
import OfficeService from '../office/officeService.js';
import DoctorInfoService from '../doctorInfo/doctorInfoService.js';

import patientFileService from '../PatientFile/PatientFileService.js';

import PatientDetails from './PatientBoard/PatientDetails.jsx';
import UserPoints from './PatientBoard/UserPoints.jsx';
import InsuranceSection from './PatientBoard/InsuranceSection.jsx';
import AuthorizationSection from './PatientBoard/AuthorizationSection.jsx';
import PatientRatings from './PatientBoard/PatientRatings.jsx';

import SimplePatientAppointments from './PatientBoard/SimplePatientAppointments.jsx';
import PatientSuperbills from './PatientBoard/PatientSuperbills.jsx';

import './ptDash.css';

const DEFAULT_DOCTOR_ID = 15;
const DEFAULT_DOCTOR_NAME = 'Default / Not Sure';

const GENERATED_EMAIL_DOMAIN = '@newupt.com';

const PatientDashboard = () => {
  const [patient, setPatient] = useState(null);
  const [editedPatient, setEditedPatient] = useState({});
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [userPoints, setUserPoints] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [permissionId, setPermissionId] = useState(null);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConsent, setShowConsent] = useState(false);

  const [role] = useState(localStorage.getItem('role') || '');

  const normalizedRole = role?.replace('ROLE_', '') || '';
  const isPatient = normalizedRole === 'PATIENT';

  const isManagerOrAdmin =
    normalizedRole === 'ADMIN' || normalizedRole === 'MANAGER';

  const isGeneratedNewUEmail = (email) => {
    return String(email || '')
      .trim()
      .toLowerCase()
      .endsWith(GENERATED_EMAIL_DOMAIN);
  };

  const canStaffEditPatient =
    isManagerOrAdmin && isGeneratedNewUEmail(patient?.email);

  const canEditPatientProfile = isPatient || canStaffEditPatient;

  const canViewAuthorizations =
    normalizedRole === 'ADMIN' ||
    normalizedRole === 'MANAGER' ||
    normalizedRole === 'FRONTDESK' ||
    normalizedRole === 'DOCTOR';

  const canViewSuperbills =
    normalizedRole === 'ADMIN' ||
    normalizedRole === 'MANAGER' ||
    normalizedRole === 'FRONTDESK' ||
    normalizedRole === 'DOCTOR';

  const canEditInsurance =
    normalizedRole === 'ADMIN' ||
    normalizedRole === 'MANAGER' ||
    normalizedRole === 'FRONTDESK' ||
    normalizedRole === 'DOCTOR';

  const canViewPointsBank =
    normalizedRole === 'ADMIN' ||
    normalizedRole === 'MANAGER' ||
    isPatient;

  const canViewContactPreferences = isPatient;

  const [patientFiles, setPatientFiles] = useState([]);
  const [filesError, setFilesError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [storageUsage, setStorageUsage] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const patientId = localStorage.getItem('patientId');

    const ptService = new PatientInfoService();
    const insService = new insuranceDetailsService();
    const insdetails = new InsurancePatientInfoService();
    const authService = new authorizationService();
    const pointService = new PointsBankAPI();
    const diagComb = new DiagnosisCombinerService();
    const permissionService = new PatientContactPermissionService();
    const officeService = new OfficeService();
    const doctorService = new DoctorInfoService();

    let interval;

    const getDoctorDisplayName = (doctor) => {
      if (!doctor) return '';

      const first =
        doctor.firstName ||
        doctor.firstname ||
        doctor.first_name ||
        '';

      const last =
        doctor.lastName ||
        doctor.lastname ||
        doctor.last_name ||
        '';

      const full = `${first} ${last}`.trim();

      return full || doctor.username || '';
    };

    const fetchStorageUsage = async () => {
      try {
        const currentPatientId = localStorage.getItem('patientId');

        if (!currentPatientId) {
          throw new Error('No patientId found for storage usage.');
        }

        let usage;

        if (isPatient) {
          usage = await patientFileService.getMyUsage(currentPatientId);
        } else {
          usage = await patientFileService.getUsageByPatientId(currentPatientId);
        }

        if (isMounted) {
          setStorageUsage(usage);
        }
      } catch (e) {
        if (isMounted) {
          setFilesError(e.message || 'Failed to load storage usage');
        }
      }
    };

    const fetchFiles = async () => {
      try {
        setFilesError('');

        const currentPatientId = localStorage.getItem('patientId');

        if (!currentPatientId) {
          throw new Error('No patientId found for patient files.');
        }

        let files = [];

        if (isPatient) {
          files = await patientFileService.listMine(currentPatientId);
        } else {
          files = await patientFileService.listByPatientId(currentPatientId);
        }

        if (isMounted) {
          setPatientFiles(files || []);
        }

        await fetchStorageUsage();
      } catch (e) {
        if (isMounted) {
          setFilesError(e.message || 'Failed to load files');
        }
      }
    };

    const fetchData = async () => {
      if (!patientId) {
        setError('No patient ID found in local storage.');
        return;
      }

      try {
        const loadedPatient = await ptService.getPatientById(patientId);

        if (isMounted) {
          setPatient(loadedPatient);
          setEditedPatient(loadedPatient);
          setUsername(loadedPatient.username || '');
        }

        if (canViewPointsBank) {
          const points = await pointService.getTotalPoints(patientId);

          if (isMounted) {
            setUserPoints(points);
          }
        }

        const insuranceRaw = await insdetails.getByPatientId(patientId);
        const insuranceCache = {};
        const officeCache = {};
        const doctorCache = {};

        for (const entry of insuranceRaw) {
          const insId = entry.insId;

          if (insId && !insuranceCache[insId]) {
            insuranceCache[insId] = await insService.getInsuranceNameById(insId);
          }

          entry.insuranceName = insuranceCache[insId] || 'N/A';

          if (entry.officeId) {
            if (!officeCache[entry.officeId]) {
              try {
                const office = await officeService.getOfficeById(entry.officeId);
                officeCache[entry.officeId] =
                  office?.officeName || `Office #${entry.officeId}`;
              } catch {
                officeCache[entry.officeId] = `Office #${entry.officeId}`;
              }
            }

            entry.officeName = officeCache[entry.officeId];
          }

          if (entry.doctorId) {
            if (Number(entry.doctorId) === DEFAULT_DOCTOR_ID) {
              doctorCache[entry.doctorId] = DEFAULT_DOCTOR_NAME;
            }

            if (!doctorCache[entry.doctorId]) {
              try {
                const doctor = await doctorService.getDoctorById(entry.doctorId);
                doctorCache[entry.doctorId] =
                  getDoctorDisplayName(doctor) || `Doctor #${entry.doctorId}`;
              } catch {
                doctorCache[entry.doctorId] = `Doctor #${entry.doctorId}`;
              }
            }

            entry.doctorName = doctorCache[entry.doctorId];
          }
        }

        if (isMounted) {
          setInsuranceData(insuranceRaw);
        }

        if (canViewAuthorizations || canViewSuperbills) {
          const authList = await authService.findByPatientId(patientId);

          for (const auth of authList) {
            try {
              const diag = await diagComb.getByAuthId(auth.authId);
              const diagRecord = Array.isArray(diag) ? diag[0] : diag;

              Object.assign(auth, {
                diagnosisSet: diagRecord?.diagnosisSet || '',
                procedureSet: diagRecord?.procedureSet || '',
                diagCombId: diagRecord?.diagCombId || null,
              });
            } catch {
              Object.assign(auth, {
                diagnosisSet: '',
                procedureSet: '',
                diagCombId: null,
              });
            }
          }

          if (isMounted) {
            setAuthorizations(authList);
          }
        } else if (isMounted) {
          setAuthorizations([]);
        }

        if (canViewContactPreferences) {
          const perms = await permissionService.getByPatientId(patientId);

          if (isMounted && perms) {
            setPermissions(perms);
            setPermissionId(perms.permissionId || perms.id || null);
          }
        }

        await fetchFiles();

        if (canViewPointsBank) {
          interval = setInterval(async () => {
            const updated = await pointService.getTotalPoints(patientId);

            if (isMounted) {
              setUserPoints(updated);
            }
          }, 30000);
        }
      } catch (e) {
        if (isMounted) {
          setError(`Failed to load data: ${e.message}`);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      clearInterval(interval);

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditToggle = () => {
    if (!canEditPatientProfile) {
      alert('This patient has a real email on file. Staff cannot edit this patient profile.');
      return;
    }

    if (!editMode && patient) {
      setEditedPatient(patient);
      setUsername(patient.username || '');
      setPassword('');
    }

    setEditMode((prev) => !prev);
  };

  const handleCancelEdit = () => {
    setEditedPatient(patient || {});
    setUsername(patient?.username || '');
    setPassword('');
    setEditMode(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setEditedPatient((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const closeStaffEditIfEmailBecameReal = (email) => {
    if (!isPatient && !isGeneratedNewUEmail(email)) {
      setEditMode(false);
    }
  };

  const handleSaveDetails = async () => {
    if (!canEditPatientProfile) {
      alert('This patient has a real email on file. Staff cannot edit this patient profile.');
      return;
    }

    try {
      const ptService = new PatientInfoService();

      const detailsPayload = {
        firstName: editedPatient.firstName || '',
        lastName: editedPatient.lastName || '',
        phoneNumber: editedPatient.phoneNumber || '',
        email: editedPatient.email || '',
        sex: editedPatient.sex || '',
        dateOfBirth: editedPatient.dateOfBirth || null,
        notes: editedPatient.notes || '',
        age: editedPatient.age ?? null,
        visits: editedPatient.visits ?? null,
        nfWc: editedPatient.nfWc ?? null,
      };

      await ptService.savePatientDetails(patient.patientId, null, detailsPayload);

      setPatient((prev) => ({
        ...prev,
        ...detailsPayload,
      }));

      setEditedPatient((prev) => ({
        ...prev,
        ...detailsPayload,
      }));

      showSuccess('Profile details updated successfully!');
      closeStaffEditIfEmailBecameReal(detailsPayload.email);
    } catch (err) {
      alert(`Error saving details: ${err.message}`);
    }
  };

  const handleUpdateAccount = async () => {
    if (!canEditPatientProfile) {
      alert('This patient has a real email on file. Staff cannot edit this patient account.');
      return;
    }

    try {
      const ptService = new PatientInfoService();

      await ptService.updatePatientAccount(
        patient.patientId,
        username,
        password,
        editedPatient.email
      );

      setPatient((prev) => ({
        ...prev,
        username,
        email: editedPatient.email,
      }));

      setPassword('');

      showSuccess('Account updated successfully!');
      closeStaffEditIfEmailBecameReal(editedPatient.email);
    } catch (err) {
      alert(`Error updating account: ${err.message}`);
    }
  };

  const handleSavePreferences = async () => {
    if (!permissionId) {
      alert('Permission ID missing.');
      return;
    }

    try {
      const service = new PatientContactPermissionService();

      await service.update(permissionId, permissions);

      showSuccess('Preferences updated successfully!');
    } catch (err) {
      alert('Failed to update preferences: ' + err.message);
    }
  };

  const refreshStorageUsage = async () => {
    try {
      const patientId = localStorage.getItem('patientId');

      if (!patientId) {
        throw new Error('No patientId found for storage usage.');
      }

      let usage;

      if (isPatient) {
        usage = await patientFileService.getMyUsage(patientId);
      } else {
        usage = await patientFileService.getUsageByPatientId(patientId);
      }

      setStorageUsage(usage);
    } catch (e) {
      setFilesError(e.message || 'Failed to refresh storage usage');
    }
  };

  const refreshFiles = async () => {
    try {
      setFilesError('');

      const patientId = localStorage.getItem('patientId');

      if (!patientId) {
        throw new Error('No patientId found for patient files.');
      }

      let files = [];

      if (isPatient) {
        files = await patientFileService.listMine(patientId);
      } else {
        files = await patientFileService.listByPatientId(patientId);
      }

      setPatientFiles(files || []);
      await refreshStorageUsage();
    } catch (e) {
      setFilesError(e.message || 'Failed to refresh files');
    }
  };

  const handleUpload = async () => {
    const patientId = localStorage.getItem('patientId');

    if (!patientId) {
      return alert('No patientId in local storage.');
    }

    if (!fileToUpload) {
      return alert('Pick a file first.');
    }

    if (storageUsage && fileToUpload.size > storageUsage.remainingBytes) {
      return alert(
        `This file is too large. Remaining storage: ${storageUsage.remainingMb} MB.`
      );
    }

    try {
      setUploading(true);

      await patientFileService.uploadFile({
        patientId: Number(patientId),
        file: fileToUpload,
        filename: uploadName || fileToUpload.name,
      });

      setFileToUpload(null);
      setUploadName('');

      await refreshFiles();

      showSuccess('File uploaded!');
    } catch (e) {
      alert(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async (publicId, displayName) => {
    try {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      const blob = await patientFileService.downloadBlob(publicId, {
        attachment: false,
      });

      const url = URL.createObjectURL(blob);

      setPreviewUrl(url);
      setPreviewName(displayName || 'Preview');
    } catch (e) {
      alert(e.message || 'Preview failed');
    }
  };

  const handleDownload = async (publicId, filename) => {
    try {
      await patientFileService.forceDownload(publicId, filename || 'download', {
        attachment: true,
      });
    } catch (e) {
      alert(e.message || 'Download failed');
    }
  };

  const handleDeleteFile = async (publicId) => {
    if (!window.confirm('Delete this file?')) {
      return;
    }

    try {
      await patientFileService.delete(publicId);
      await refreshFiles();
      showSuccess('File deleted.');
    } catch (e) {
      alert(e.message || 'Delete failed');
    }
  };

  const handleRenameFile = async (publicId) => {
    const newName = window.prompt('New filename include extension:');

    if (!newName) {
      return;
    }

    try {
      await patientFileService.rename(publicId, newName);
      await refreshFiles();
      showSuccess('File renamed.');
    } catch (e) {
      alert(e.message || 'Rename failed');
    }
  };

  const getUsagePercent = () => {
    if (!storageUsage || !storageUsage.maxBytes) {
      return 0;
    }

    return Math.min(
      100,
      Math.round((storageUsage.usedBytes / storageUsage.maxBytes) * 100)
    );
  };

  if (error) {
    return (
      <div className="dashboard-container">
        <p className="error">{error}</p>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="dashboard-container">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <h1 className="title">Patient Dashboard</h1>

      {successMessage && (
        <div className="success-message">✅ {successMessage}</div>
      )}

      <div className="dashboard-grid">
        <div className="info-box">
          {editMode ? (
            <>
              <div className="profile-edit-section">
                <h3>Profile Details</h3>

                {!isPatient && canStaffEditPatient && (
                  <p className="section-help">
                    Staff editing is allowed because this patient still has a generated New U email.
                    Once the email is changed to a real email, staff editing will be locked.
                  </p>
                )}

                <p className="section-help">
                  Update personal and contact information here.
                </p>

                <input
                  type="text"
                  name="firstName"
                  value={editedPatient.firstName || ''}
                  onChange={handleInputChange}
                  placeholder="First Name"
                  className="edit-input"
                />

                <input
                  type="text"
                  name="lastName"
                  value={editedPatient.lastName || ''}
                  onChange={handleInputChange}
                  placeholder="Last Name"
                  className="edit-input"
                />

                <input
                  type="tel"
                  name="phoneNumber"
                  value={editedPatient.phoneNumber || ''}
                  onChange={handleInputChange}
                  placeholder="Phone Number"
                  className="edit-input"
                />

                <input
                  type="email"
                  name="email"
                  value={editedPatient.email || ''}
                  onChange={handleInputChange}
                  placeholder="Email"
                  className="edit-input"
                />

                <select
                  name="sex"
                  value={editedPatient.sex || ''}
                  onChange={handleInputChange}
                  className="edit-input"
                >
                  <option value="">Select Sex</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>

                <label className="edit-label">
                  Date of Birth
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={editedPatient.dateOfBirth || ''}
                    onChange={handleInputChange}
                    className="edit-input"
                  />
                </label>

                <textarea
                  name="notes"
                  value={editedPatient.notes || ''}
                  onChange={handleInputChange}
                  placeholder="Notes"
                  className="edit-input"
                  rows="3"
                />

                <button onClick={handleSaveDetails} className="save-button">
                  Save Profile Details
                </button>
              </div>

              <hr className="edit-divider" />

              <div className="account-edit-section">
                <h3>Account Security</h3>
                <p className="section-help">
                  Change login username or password here. Leave password blank
                  if you do not want to change it.
                </p>

                <input
                  type="text"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Username"
                  className="edit-input"
                />

                <input
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New Password"
                  className="edit-input"
                />

                <button
                  onClick={handleUpdateAccount}
                  className="password-button"
                >
                  Update Username / Password
                </button>
              </div>

              <button onClick={handleCancelEdit} className="cancel-button">
                Cancel
              </button>
            </>
          ) : (
            <>
              <PatientDetails patient={patient} />

              {canEditPatientProfile && (
                <button onClick={handleEditToggle} className="edit-button">
                  Edit Profile
                </button>
              )}

              {!isPatient && isManagerOrAdmin && !canStaffEditPatient && (
                <p className="section-help">
                  Staff editing is locked because this patient has a real email on file.
                </p>
              )}
            </>
          )}
        </div>

        {canViewPointsBank && (
          <div className="info-box">
            <UserPoints
              points={userPoints}
              editable={
                normalizedRole === 'ADMIN' || normalizedRole === 'MANAGER'
              }
            />
          </div>
        )}

        <div className="info-box">
          <InsuranceSection
            insuranceData={insuranceData}
            editable={canEditInsurance}
          />
        </div>

        {canViewAuthorizations && (
          <div className="info-box">
            <AuthorizationSection
              authorizations={authorizations}
              userRole={normalizedRole}
            />
          </div>
        )}

        <div className="info-box">
          <h3>📄 Patient Files</h3>

          {filesError && <p className="error">{filesError}</p>}

          {storageUsage && (
            <div
              style={{
                marginBottom: 12,
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: '#f9fafb',
              }}
            >
              <strong>Storage used:</strong>{' '}
              {storageUsage.usedMb} MB / {storageUsage.maxMb} MB

              <div
                style={{
                  marginTop: 8,
                  width: '100%',
                  height: 10,
                  background: '#e5e7eb',
                  borderRadius: 999,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${getUsagePercent()}%`,
                    height: '100%',
                    background:
                      getUsagePercent() >= 90
                        ? '#dc2626'
                        : getUsagePercent() >= 70
                        ? '#f59e0b'
                        : '#16a34a',
                  }}
                />
              </div>

              <small>Remaining: {storageUsage.remainingMb} MB</small>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <input
              type="file"
              onChange={(e) => setFileToUpload(e.target.files?.[0] || null)}
            />

            <input
              type="text"
              placeholder="Optional filename ex: MRI.pdf"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              className="edit-input"
              style={{ maxWidth: 260 }}
            />

            <button
              onClick={handleUpload}
              className="save-button"
              disabled={
                uploading ||
                !fileToUpload ||
                (storageUsage && storageUsage.remainingBytes <= 0)
              }
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>

            <button onClick={refreshFiles} className="edit-button">
              Refresh
            </button>
          </div>

          {fileToUpload && (
            <small style={{ display: 'block', marginTop: 8 }}>
              Selected file: {fileToUpload.name} —{' '}
              {Math.round(fileToUpload.size / 1024 / 1024)} MB
            </small>
          )}

          <div style={{ marginTop: 12 }}>
            {patientFiles.length === 0 ? (
              <p>No files yet.</p>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                {patientFiles.map((f) => (
                  <div
                    key={f.publicId}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 10,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <strong>{f.displayName}</strong>

                      <small style={{ opacity: 0.7 }}>
                        {f.contentType || 'file'} •{' '}
                        {f.sizeBytes ? Math.round(f.sizeBytes / 1024) : 0} KB
                      </small>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <button
                        className="edit-button"
                        onClick={() => handlePreview(f.publicId, f.displayName)}
                      >
                        View
                      </button>

                      <button
                        className="password-button"
                        onClick={() =>
                          handleDownload(f.publicId, f.displayName)
                        }
                      >
                        Download
                      </button>

                      <button
                        className="cancel-button"
                        onClick={() => handleRenameFile(f.publicId)}
                      >
                        Rename
                      </button>

                      <button
                        className="cancel-button"
                        onClick={() => handleDeleteFile(f.publicId)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {previewUrl && (
            <div style={{ marginTop: 12 }}>
              <h4 style={{ marginBottom: 8 }}>Preview: {previewName}</h4>

              <iframe
                title="file-preview"
                src={previewUrl}
                style={{
                  width: '100%',
                  height: 420,
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                }}
              />

              <button
                className="cancel-button"
                style={{ marginTop: 10 }}
                onClick={() => {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl('');
                  setPreviewName('');
                }}
              >
                Close Preview
              </button>
            </div>
          )}
        </div>

        <SimplePatientAppointments patientId={patient.patientId} />

        {canViewSuperbills && (
          <PatientSuperbills
            patientId={patient.patientId}
            authorizations={authorizations}
          />
        )}

        {canViewSuperbills && <PatientRatings authorizations={authorizations} />}

        {canViewContactPreferences && (
          <div className="info-box communication-box">
            <div
              className="toggle-header"
              onClick={() => setShowConsent((prev) => !prev)}
            >
              <h3>📬 Communication Preferences</h3>
              <span>{showConsent ? '▲' : '▼'}</span>
            </div>

            {showConsent && (
              <div className="consent-body">
                {[
                  {
                    title: 'Promotions/Messages',
                    ids: ['allowPromotionText', 'allowPromotionEmail'],
                    description:
                      'By checking these boxes, you agree to receive promotional content and messages via SMS or email.',
                  },
                  {
                    title: 'Appointment Reminders',
                    ids: [
                      'allowAppointmentReminderText',
                      'allowAppointmentReminderEmail',
                    ],
                    description:
                      'Reminders will be sent the day of your appointment.',
                  },
                  {
                    title: 'Authorization Notifications',
                    ids: [
                      'allowAuthorizationReminderText',
                      'allowAuthorizationReminderEmail',
                    ],
                    description:
                      'We’ll notify you before insurance authorization expires or runs out.',
                  },
                ].map(({ title, ids, description }) => (
                  <div key={title} className="checkbox-row">
                    <p>
                      <strong>{title}</strong>
                    </p>

                    <small>{description}</small>

                    <div className="checkbox-group">
                      {ids.map((id) => (
                        <label key={id} className="checkbox-column">
                          <input
                            type="checkbox"
                            checked={!!permissions[id]}
                            disabled={!isPatient}
                            onChange={(e) =>
                              setPermissions((prev) => ({
                                ...prev,
                                [id]: e.target.checked,
                              }))
                            }
                          />

                          {id.includes('Text')
                            ? `${title} via text`
                            : `${title} via email`}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                {isPatient && (
                  <button
                    onClick={handleSavePreferences}
                    className="save-button"
                  >
                    Save Preferences
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientDashboard;